<template>
  <div class="landing-ios">
    <div class="container">
      <h1 class="landing-ios__title h1_title text-center">
        {{ $t('lp_email_plan_ready_title') }}
      </h1>
      <p class="landing-ios__subtitle expect-text paragraph-16-400 text-center">
        {{ $t('lp_v7_when_expect_improvements_body') }}
      </p>
      <FinalGraph
        :faster-label="false"
        :final-date="finalDate"
        :label="$t('lp_v7_illustration_note')"
        class="landing-ios__final-graph"
      />
      <ImprovementAreas
        class="landing-ios__areas-improvements"
        :title="$t('lp_v7_improvement_areas_title')"
        :options="areasOptions"
      />
    </div>
    <div class="landing-ios__price-section">
      <PriceInfo
        :title="$t('lp_email_priceToday_title')"
        :price-list="config?.priceToday"
        :description="$t('lp_email_priceToday_description', [config?.trial])"
      />
      <PriceInfo
        v-if="upsaleAvailable"
        :title="$t('lp_email_priceUpgrade_title')"
        :price-list="config?.priceUpgrade"
      />
      <PriceInfo
        :title="$t('lp_email_priceAfterTrial_title')"
        :price-list="config?.priceAfterTrial"
        :description="$t('lp_email_priceAfterTrial_description', [lastDate])"
      />
    </div>
    <div class="container">
      <div id="payment" class="landing-ios__payment-section">
        <div class="landing-ios__payment-section-title">
          <h2>{{ $t('lp_email_payment_method') }}</h2>
          <button @click="openWhyNowModal">
            {{ $t('lp_email_whyNow') }}
          </button>
        </div>
        <BasicCheckoutForm
          :configs="config?.prices.standard"
          :show-description="false"
          :is-email-landing="true"
          style="padding: 0; height: auto"
          @on-success="onPaymentSuccess"
        />
      </div>
      <RatingsDownloads
        :rate="$t('lp_email_ratings_rate')"
        :downloads="$t('lp_email_ratings_downloads')"
        :ratings="$t('lp_email_ratings_rating')"
        class="landing-ios__rating-downloads"
      />
      <div class="landing-ios__guarantees-list">
        <div class="landing-ios__guarantees-item">
          <h2>{{ $t('lp_email_guar1_title') }}</h2>
          <p v-html="$t('lp_email_guar1_text')" />
        </div>
        <div class="landing-ios__guarantees-item">
          <h2>{{ $t('lp_email_guar2_title') }}</h2>
          <p v-html="$t('lp_email_guar2_text')" />
        </div>
        <div class="landing-ios__guarantees-item">
          <h2>{{ $t('lp_email_guar3_title') }}</h2>
          <p v-html="$t('lp_email_guar3_text')" />
        </div>
        <div class="landing-ios__guarantees-item">
          <h2>{{ $t('lp_email_guar4_title') }}</h2>
          <p v-html="$t('lp_email_guar4_text', ['contact@kegel-plan.com'])" />
        </div>
      </div>
      <base-button
        type="red"
        with-shadow
        class="landing-ios__cta"
        :is-bold="false"
        @click="scrollToPayment"
      >
        {{ $t('lp_v7_get_plan_button1') }}
      </base-button>
      <p
        class="landing-ios__disclaimer"
        v-html="
          $t(config.disclaimerKey, [
            config?.trial,
            lastDate,
            config?.prices.fullPrice_new,
          ])
        "
      />
    </div>

    <teleport to="body">
      <LazyBaseModal v-if="dialogStore.showDialog" />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import Modals from './modal/enums';

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },
});

const route = useRoute();
const { scrollTo } = useScrollTo();
const { openModal } = useModals();
const dayjs = useDayjs();
const { t, locale } = useI18n();
const finalDate = dayjs().locale(locale.value).add(28, 'day').format('MMMM D');
const lastDate = dayjs()
  .locale(locale.value)
  .add(props.config?.trial, 'days')
  .format('MMMM D, YYYY, h:mm a');
const router = useRouter();
const localePath = useLocalePath();
const dialogStore = useDialogStore();
const store = useKegelAnalyticStore();
const upsaleAvailable = computed(
  () => typeof props.config?.upsale === 'boolean' && !props.config?.upsale,
);
const { erectile, improve } = useAppConfig().improveAreas;

const areasOptions = computed(() => {
  return [...erectile, ...improve];
});

function openWhyNowModal() {
  openModal(Modals.whyNow.component, {
    ...Modals.whyNow.props,
    action: scrollToPayment,
    texts: {
      title: t('lp_email_whyNow'),
      text: t('lp_email_modal_whyNow_text', [props.config.trial]),
      cta: t('super_discount_got_it_button'),
    },
  });
}

function onPaymentSuccess() {
  let path = '/verify-email';

  if (props.config?.upsale) {
    path = '/fitness/basic-discount';
  } else if (locale.value === 'en') {
    path = '/courses/sexology';
  }

  store.subscriptionInfo = props.config?.prices;
  router.push(
    localePath({
      path,
      query: { ...route.query, pID: props.config?.prices?.id },
    }),
  );
}

function scrollToPayment() {
  if (import.meta.client) {
    const paymentSection = document.getElementById('payment');
    scrollTo(paymentSection);
  }
}
</script>

<style lang="scss" scoped>
.landing-ios {
  padding-bottom: 64px;
  &__title {
    margin-bottom: 24px;
    font-weight: 700;
  }
  &__subtitle {
    margin-bottom: 16px;
    font-weight: 400;
  }

  &__final-graph {
    margin-bottom: 32px;
  }

  &__areas-improvements {
    margin-bottom: 48px;
  }

  &__price-section {
    margin-bottom: 48px;
  }

  &__payment-section {
    margin-bottom: 48px;
  }

  &__payment-section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 16px;
    margin-bottom: 24px;

    border-bottom: 1px solid $gray-bg;

    & h2 {
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 150%; /* 24px */
    }

    & button {
      border: none;
      background: transparent;
      font-family: $primary-fonts;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%; /* 21px */
      padding: 0;
      opacity: 0.75;
      cursor: pointer;
    }
  }

  &__rating-downloads {
    margin-bottom: 48px;
  }

  &__guarantees-list {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-bottom: 48px;
  }

  &__guarantees-item {
    & h2 {
      font-size: 16px;
      font-style: normal;
      font-weight: 700;
      line-height: 135%; /* 21.6px */
      margin-bottom: 12px;
    }

    & p {
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%; /* 21px */

      & :deep(.red) {
        color: $red;
        font-weight: 600;
      }
    }
  }

  &__cta {
    margin-bottom: 48px;
  }

  &__disclaimer {
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 18px */
    opacity: 0.5;

    & :deep(.bold) {
      font-weight: 600;
    }
  }
}
</style>
